import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAppState } from '@/hooks/useAppState';
import { useToastMessages } from '@/hooks/useToastMessages';
import { AccountService } from '@/services/domain/AccountService';
import type { AccountSettingsResponse } from '@/services/domain/AccountTypes';

export const ACCOUNT_SETTINGS_QUERY_KEY = ['accountSettings'] as const;

const ACCOUNT_SETTINGS_STALE_TIME = 5 * 60 * 1000;

interface IUpdateAccountSettingVariables {
  key: keyof AccountSettingsResponse;
  value: boolean;
  onSuccess?: () => void;
}

interface IUseAccountSettingsOptions {
  enabled?: boolean;
}

export function useAccountSettings(options: IUseAccountSettingsOptions = {}) {
  const { enabled = true } = options;
  const { t } = useTranslation();
  const { showErrorToast } = useToastMessages();
  const hasToastedFetchErrorRef = useRef(false);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ACCOUNT_SETTINGS_QUERY_KEY,
    queryFn: AccountService.getAccountSettings,
    staleTime: ACCOUNT_SETTINGS_STALE_TIME,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: Platform.OS === 'web',
    enabled,
  });

  const refreshAccountSettings = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ACCOUNT_SETTINGS_QUERY_KEY });
  }, [queryClient]);

  useAppState({ onForeground: refreshAccountSettings });

  useEffect(() => {
    if (query.isError && query.error && !hasToastedFetchErrorRef.current) {
      hasToastedFetchErrorRef.current = true;
      showErrorToast(t('common.error.general'), query.error);
    }

    if (query.isSuccess) {
      hasToastedFetchErrorRef.current = false;
    }
  }, [query.error, query.isError, query.isSuccess, showErrorToast, t]);

  return query;
}

export function useUpdateAccountSetting() {
  const { t } = useTranslation();
  const { showErrorToast } = useToastMessages();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ key, value }: IUpdateAccountSettingVariables) =>
      AccountService.updateAccountSetting({ Key: key, IsEnabled: value }),
    onMutate: async ({ key, value }) => {
      await queryClient.cancelQueries({ queryKey: ACCOUNT_SETTINGS_QUERY_KEY });

      const previousSettings = queryClient.getQueryData<AccountSettingsResponse>(
        ACCOUNT_SETTINGS_QUERY_KEY
      );

      if (previousSettings) {
        queryClient.setQueryData<AccountSettingsResponse>(ACCOUNT_SETTINGS_QUERY_KEY, {
          ...previousSettings,
          [key]: value,
        });
      }

      return { previousSettings };
    },
    onError: (error, _variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(ACCOUNT_SETTINGS_QUERY_KEY, context.previousSettings);
      }
      showErrorToast(t('common.error.general'), error);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_SETTINGS_QUERY_KEY });
      variables.onSuccess?.();
    },
  });

  const updateSetting = useCallback(
    (
      key: keyof AccountSettingsResponse,
      value: boolean,
      options?: Pick<IUpdateAccountSettingVariables, 'onSuccess'>
    ) => {
      mutation.mutate({ key, value, onSuccess: options?.onSuccess });
    },
    [mutation]
  );

  return { updateSetting, isUpdating: mutation.isPending };
}
